import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import NodeGlyph from "../revenue-path/NodeGlyph";

const REVIEWS = [
  { label: "How leads contact you", state: "healthy" },
  { label: "How fast they get a response", state: "friction" },
  { label: "How booking happens", state: "leak" },
  { label: "How payment is handled", state: "broken" },
  { label: "How follow-up works", state: "leak" },
  { label: "Where prospects fall off", state: "friction" },
  { label: "What should be fixed first", state: "recovered" },
];

/**
 * "Start with the leak" — the diagnostic front-door offer near the top of the
 * homepage. Routes to the dedicated /revenue-leak-audit page.
 */
export default function RevenueLeakAuditSection() {
  return (
    <section
      id="revenue-leak-audit"
      data-testid="revenue-leak-audit-section"
      className="bg-sand-100 px-5 sm:px-6 md:px-10 py-16 sm:py-20 md:py-24 scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto">
        {/* threshold marker */}
        <div className="reveal flex items-center gap-3 mb-10 sm:mb-12">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay-deep whitespace-nowrap">
            Threshold · The Diagnostic
          </span>
          <span className="h-px flex-1 bg-sand-300" />
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-6 reveal">
            <p className="eyebrow">The Front Door</p>
            <h2
              data-kinetic
              className="font-display mt-4 sm:mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3.1rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink"
            >
              Start with{" "}
              <span className="italic font-normal text-ink-muted">the leak.</span>
            </h2>
            <p className="mt-5 sm:mt-6 text-base md:text-lg text-ink-muted leading-relaxed">
              Most service businesses do not need more random tools. They need
              to know where the money is slipping.
            </p>
            <p className="mt-4 text-base md:text-lg text-ink-muted leading-relaxed">
              The Revenue Leak Audit reviews your current path from first contact
              to booked, paid, and followed up.
            </p>
            <Link
              to="/revenue-leak-audit"
              data-testid="leak-section-cta"
              data-magnetic
              className="mt-8 group inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-ink transition-colors min-h-[52px]"
            >
              Get a Revenue Leak Audit
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </div>

          <div className="lg:col-span-6 reveal">
            <div className="border border-sand-300 bg-sand-50 p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-deep">
                  We look at
                </p>
                <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-muted/70">
                  SYSTEM_SCAN
                </p>
              </div>
              <ul className="mt-5 divide-y divide-sand-300/70">
                {REVIEWS.map((r, idx) => (
                  <li
                    key={r.label}
                    data-testid={`leak-review-${idx}`}
                    className="flex items-center gap-4 py-3.5"
                  >
                    <span className="font-mono text-[11px] text-ink-muted/70 tabular-nums w-5">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 text-[14.5px] sm:text-[15px] text-ink leading-snug">
                      {r.label}
                    </span>
                    <NodeGlyph state={r.state} size={18} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
